import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { sendTransactionSync } from '@wagmi/core'
import { ExecutionError, Rpc } from 'accounts'
import { type Address, Hex } from 'ox'
import { useEffect, useMemo, useState } from 'react'
import { formatHash } from 'regen-ui'
import { formatUnits, isAddress, parseUnits } from 'viem'
import { Addresses, Actions } from 'viem/tempo'
import { useConnection, usePrepareTransactionRequest } from 'wagmi'
import { Hooks } from 'wagmi/tempo'
import * as z from 'zod/mini'

import { useAssets } from '#/lib/assets.js'
import { getSupportedChainId } from '#/lib/chain.js'
import { wagmiConfig } from '#/lib/config.js'
import { useDebounce } from '#/lib/react.js'
import * as SwapAssets from '#/lib/swap-assets.js'
import * as SwapPolicy from '#/lib/swap.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as Token from '#/lib/token.js'
import { revertedTxHash } from '#/lib/transaction.js'
import * as TransactionFrames from '../-frames/transaction/index.js'
import { usePrepareAlert } from '../-hooks/usePrepareAlert.js'
import { useRequireFunds } from '../-hooks/useRequireFunds.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'
import * as SameSite from '../-lib/SameSite.js'

const decimalRegex = /^\d*\.?\d*$/
const defaultSlippage = '0.005'
const slippageScale = 1_000_000n
const tip20Decimals = 6

export const Route = createFileRoute('/remote/rpc/wallet_swap')({
  component: Component,
  validateSearch: (search) => RemoteRpc.validateSearch(search, { method: 'wallet_swap' }),
})

function Component() {
  const { capture } = useAnalytics()
  const request = Route.useSearch()
  const rpc = RemoteRpc.use()
  const { address, chainId } = useConnection()

  const params = request._decoded?.params?.[0]
  const resolvedChainId = getSupportedChainId(chainId)
  const initialType = params?.type
  const type = initialType ?? 'sell'
  const frameMode: 'exactIn' | 'exactOut' = type === 'buy' ? 'exactOut' : 'exactIn'
  const [values, setValues] = useState(() => ({
    amount: params?.amount ?? '',
    pairToken: params?.pairToken ?? '',
    token: params?.token ?? '',
  }))

  const fallbackAssets = useMemo(
    () =>
      Token.list({ chainId: resolvedChainId }).filter(
        (asset) => asset.address.toLowerCase() !== Addresses.pathUsd.toLowerCase(),
      ),
    [resolvedChainId],
  )
  const requestedTokens = useMemo(() => {
    const tokens = [
      values.token,
      values.pairToken,
      Addresses.pathUsd,
      ...fallbackAssets.map((asset) => asset.address),
    ]
    const seen = new Set<string>()
    return tokens
      .filter((token): token is Address.Address => !!token && isAddress(token))
      .filter((token) => {
        const key = token.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
  }, [fallbackAssets, values.pairToken, values.token])

  const assets = useAssets({
    account: address,
    chainId: resolvedChainId,
    fresh: true,
    tokens: requestedTokens,
  })

  const heldAssets = useMemo(() => {
    if (!assets.data) return undefined
    return assets.data.filter((asset) => Token.isTip20(asset.address))
  }, [assets.data])

  const effectiveToken = useMemo(() => {
    if (isAddress(values.token)) return values.token as Address.Address
    return SwapAssets.defaultInputToken(heldAssets, Addresses.pathUsd)
  }, [heldAssets, values.token])

  const effectivePairToken = useMemo(() => {
    if (isAddress(values.pairToken)) return values.pairToken as Address.Address
    if (effectiveToken.toLowerCase() !== Addresses.pathUsd.toLowerCase()) return Addresses.pathUsd
    return heldAssets?.find((asset) => !sameAddress(asset.address, effectiveToken))?.address ?? ''
  }, [effectiveToken, heldAssets, values.pairToken])

  const effectiveTokenIn = type === 'buy' ? effectivePairToken : effectiveToken
  const effectiveTokenOut = type === 'buy' ? effectiveToken : effectivePairToken

  const tokenMetadata = Hooks.token.useGetMetadata({
    token: effectiveToken as Hex.Hex,
    query: {
      enabled:
        isAddress(effectiveToken) &&
        !sameAddress(effectiveToken, Addresses.pathUsd) &&
        !fallbackAssets.some((asset) => sameAddress(asset.address, effectiveToken)) &&
        !findAsset(heldAssets, effectiveToken),
      retry: false,
    },
  })
  const pairTokenMetadata = Hooks.token.useGetMetadata({
    token: effectivePairToken as Hex.Hex,
    query: {
      enabled:
        isAddress(effectivePairToken) &&
        !sameAddress(effectivePairToken, Addresses.pathUsd) &&
        !fallbackAssets.some((asset) => sameAddress(asset.address, effectivePairToken)) &&
        !findAsset(heldAssets, effectivePairToken),
      retry: false,
    },
  })

  const assetOptions = useMemo(() => {
    const options = new Map<string, SwapAsset>()
    for (const asset of heldAssets ?? []) {
      options.set(asset.address.toLowerCase(), {
        address: asset.address,
        balance: BigInt(asset.balance),
        decimals: asset.decimals,
        symbol: asset.symbol,
      })
    }

    upsertFallback(options, Addresses.pathUsd, undefined)
    for (const asset of fallbackAssets) upsertFallback(options, asset.address, asset)
    if (isAddress(effectiveToken)) upsertFallback(options, effectiveToken, tokenMetadata.data)
    if (isAddress(effectivePairToken))
      upsertFallback(options, effectivePairToken, pairTokenMetadata.data)

    return [...options.values()].sort((a, b) => {
      if (a.balance === b.balance) return a.symbol.localeCompare(b.symbol)
      return a.balance > b.balance ? -1 : 1
    })
  }, [
    effectivePairToken,
    effectiveToken,
    fallbackAssets,
    heldAssets,
    pairTokenMetadata.data,
    tokenMetadata.data,
  ])

  const assetToken = findAsset(assetOptions, effectiveToken)
  const assetIn = findAsset(assetOptions, effectiveTokenIn)
  const assetOut = findAsset(assetOptions, effectiveTokenOut)
  const assetOptionsIn = useMemo(() => SwapAssets.inputOptions(assetOptions), [assetOptions])
  const amountAsset = assetToken
  const amount = useMemo(
    () => parseAmount(values.amount, amountAsset?.decimals),
    [amountAsset?.decimals, values.amount],
  )
  const slippage = params?.slippage ?? Number(defaultSlippage)

  const tokenInError = useMemo(() => {
    if (!isAddress(effectiveTokenIn)) return 'Select an asset to sell.'
    if (!Token.isTip20(effectiveTokenIn)) return 'Only TIP-20 assets can be swapped.'
    if (sameAddress(effectiveTokenIn, effectiveTokenOut)) return 'Choose different assets.'
    return SwapAssets.inputBalanceError(assetIn)
  }, [assetIn, effectiveTokenIn, effectiveTokenOut])
  const tokenOutError = useMemo(() => {
    if (!isAddress(effectiveTokenOut)) return 'Select an asset to receive.'
    if (!Token.isTip20(effectiveTokenOut)) return 'Only TIP-20 assets can be swapped.'
    return undefined
  }, [effectiveTokenOut])
  const basicAmountError = useMemo(() => {
    if (!values.amount) return undefined
    if (!decimalRegex.test(values.amount) || values.amount === '.') return 'Enter a valid amount.'
    if (!amountAsset) return undefined
    const decimals = values.amount.split('.')[1]?.length ?? 0
    if (decimals > amountAsset.decimals) return `Use at most ${amountAsset.decimals} decimals.`
    if (amount === undefined) return 'Enter a valid amount.'
    if (amount <= 0n) return 'Enter an amount greater than 0.'
    return undefined
  }, [amount, amountAsset, values.amount])

  const canQuote =
    !!amount &&
    amount > 0n &&
    !!assetIn &&
    !!assetOut &&
    !basicAmountError &&
    !tokenInError &&
    !tokenOutError

  const sellQuote = Hooks.dex.useSellQuote({
    amountIn: frameMode === 'exactIn' && amount ? amount : 0n,
    chainId: resolvedChainId,
    tokenIn: effectiveTokenIn as Address.Address,
    tokenOut: effectiveTokenOut as Address.Address,
    query: {
      enabled: frameMode === 'exactIn' && canQuote,
      refetchOnWindowFocus: false,
      retry: false,
    },
  })
  const buyQuote = Hooks.dex.useBuyQuote({
    amountOut: frameMode === 'exactOut' && amount ? amount : 0n,
    chainId: resolvedChainId,
    tokenIn: effectiveTokenIn as Address.Address,
    tokenOut: effectiveTokenOut as Address.Address,
    query: {
      enabled: frameMode === 'exactOut' && canQuote,
      refetchOnWindowFocus: false,
      retry: false,
    },
  })

  const quote = frameMode === 'exactIn' ? sellQuote : buyQuote
  const quoted = quote.data
  const boundedQuote = useMemo(() => {
    if (quoted === undefined) return undefined
    if (frameMode === 'exactIn') return applySlippageDown(quoted, slippage)
    return applySlippageUp(quoted, slippage)
  }, [frameMode, quoted, slippage])
  const amountSpend = frameMode === 'exactIn' ? amount : boundedQuote
  const amountInputError = useMemo(() => {
    if (basicAmountError) return basicAmountError
    if (
      frameMode === 'exactIn' &&
      assetIn &&
      amountSpend !== undefined &&
      amountSpend > assetIn.balance
    )
      return `Insufficient ${assetIn.symbol} balance.`
    if (frameMode === 'exactIn' && boundedQuote === 0n) return 'Enter a larger amount.'
    return undefined
  }, [amountSpend, assetIn, basicAmountError, boundedQuote, frameMode])

  const calls = useMemo(() => {
    if (!amount || !assetIn || !assetOut || boundedQuote === undefined) return undefined
    if (amountSpend === undefined) return undefined
    if (amountInputError || tokenInError || tokenOutError) return undefined
    const approve = Actions.token.approve.call({
      amount: amountSpend,
      spender: Addresses.stablecoinDex,
      token: assetIn.address,
    })
    const swap =
      frameMode === 'exactIn'
        ? Actions.dex.sell.call({
            amountIn: amount,
            minAmountOut: boundedQuote,
            tokenIn: assetIn.address,
            tokenOut: assetOut.address,
          })
        : Actions.dex.buy.call({
            amountOut: amount,
            maxAmountIn: boundedQuote,
            tokenIn: assetIn.address,
            tokenOut: assetOut.address,
          })
    return [approve, swap]
  }, [
    amount,
    amountSpend,
    amountInputError,
    assetIn,
    assetOut,
    boundedQuote,
    frameMode,
    tokenInError,
    tokenOutError,
  ])

  const debouncedCalls = useDebounce(calls, 300)
  // Cross-site apps don't get the wallet's relay-funded sponsorship.
  const sameSite = SameSite.useValidate(rpc.consumer.origin)
  const prepare = usePrepareTransactionRequest({
    calls: debouncedCalls,
    // Skip the relay's post-fill `tempo_simulateV1` (~250-400ms). The
    // `balanceDiffs` capability is unused in this UI.
    capabilities: { balanceDiffs: false, errors: true },
    chainId: resolvedChainId,
    feePayer: (sameSite ? undefined : false) as never,
    to: Addresses.stablecoinDex,
    type: 'tempo',
    query: {
      enabled: !!address && !!debouncedCalls,
      refetchOnWindowFocus: false,
      refetchInterval: (query) => (query.state.data?._capabilities?.requireFunds ? 2_000 : false),
      retry: false,
      staleTime: 0,
      gcTime: 0,
    },
  })

  const prepareData = prepare.data
  const capabilities = prepare.data?._capabilities
  const { fee, sponsor, sponsored: sponsoredCap } = capabilities ?? {}
  const sponsored = !!sponsor || !!sponsoredCap
  const autoSwap = useMemo(() => {
    return SwapPolicy.autoSwap({
      candidate: capabilities?.autoSwap,
      mode: frameMode,
      tokenIn: effectiveTokenIn,
      tokenOut: effectiveTokenOut,
    })
  }, [capabilities?.autoSwap, effectiveTokenIn, effectiveTokenOut, frameMode])
  const amountError = useMemo(() => {
    if (amountInputError) return amountInputError
    if (assetIn && amountSpend !== undefined && amountSpend > assetIn.balance && !autoSwap)
      return `Insufficient ${assetIn.symbol} balance.`
    return undefined
  }, [amountInputError, amountSpend, assetIn, autoSwap])
  const funds = useRequireFunds({
    address,
    autoSwap,
    chainId: resolvedChainId,
    prepareQuery: prepare,
  })
  const preparingCurrentInput = calls !== debouncedCalls || prepare.isFetching || quote.isFetching
  const analyticsContext = useMemo(
    () => ({
      // Stablecoin DEX inputs are USD-pegged, so format to a decimal == USD value.
      amount: toUsdAmount(amount, amountAsset?.decimals),
      chainId: resolvedChainId,
      initialAmount: params?.amount,
      initialSlippage: params?.slippage,
      initialTokenIn: type === 'buy' ? params?.pairToken : params?.token,
      initialTokenOut: type === 'buy' ? params?.token : params?.pairToken,
      initialType,
      method: request.method,
      slippage,
      sponsored: capabilities ? sponsored : undefined,
      tokenIn: isAddress(effectiveTokenIn) ? effectiveTokenIn : undefined,
      tokenInSymbol: assetIn?.symbol,
      tokenOut: isAddress(effectiveTokenOut) ? effectiveTokenOut : undefined,
      tokenOutSymbol: assetOut?.symbol,
      type,
    }),
    [
      amount,
      amountAsset?.decimals,
      assetIn?.symbol,
      assetOut?.symbol,
      capabilities,
      effectiveTokenIn,
      effectiveTokenOut,
      initialType,
      params?.amount,
      params?.pairToken,
      params?.slippage,
      params?.token,
      request.method,
      resolvedChainId,
      slippage,
      sponsored,
      type,
    ],
  )

  const confirm = useMutation({
    mutationFn: async () => {
      if (preparingCurrentInput) throw new Error('Swap not prepared for current inputs.')
      if (!prepareData) throw new Error('Swap not prepared.')
      const swapCalls = autoSwap?.calls
      const data = swapCalls
        ? { ...prepareData, calls: [...swapCalls, ...(prepareData.calls ?? [])] }
        : prepareData
      const receipt = await sendTransactionSync(wagmiConfig, data as never)
      capture('swap_success', {
        ...analyticsContext,
        txHash: receipt.transactionHash,
      })
      await rpc.resolve({ receipt: z.encode(Rpc.receipt as never, receipt as never) })
      return receipt
    },
    onError: (error) => {
      const txHash = revertedTxHash(error)
      capture('swap_failure', {
        ...analyticsContext,
        error: error.message,
        errorName: error.name,
        phase: 'confirm',
        ...(txHash ? { txHash } : {}),
      })
    },
  })

  useEffect(() => {
    capture('swap_started', analyticsContext)
  }, [])

  useEffect(() => {
    if (!prepare.isError) return
    capture('swap_blocked', {
      ...analyticsContext,
      error: prepare.error.message,
      errorName: prepare.error.name,
      phase: 'prepare',
    })
  }, [prepare.isError])

  useEffect(() => {
    if (!quote.isError) return
    capture('swap_blocked', {
      ...analyticsContext,
      error: quote.error.message,
      errorName: quote.error.name,
      phase: 'quote',
    })
  }, [quote.isError])

  useEffect(() => {
    const err = capabilities?.error
    if (!err) return
    capture('swap_blocked', {
      ...analyticsContext,
      error: err.message,
      errorName: err.errorName,
      phase: 'prepare',
    })
  }, [!!capabilities?.error])

  const frameAssets = useMemo(
    () => assetOptions.map((asset) => toFrameAsset(asset)),
    [assetOptions],
  )
  const frameAssetsIn = useMemo(
    () => assetOptionsIn.map((asset) => toFrameAsset(asset)),
    [assetOptionsIn],
  )
  const quoteAmount = useMemo(() => {
    if (boundedQuote === undefined) return undefined
    const asset = frameMode === 'exactIn' ? assetOut : assetIn
    if (asset)
      return {
        decimals: asset.decimals,
        formatted: formatUnits(boundedQuote, asset.decimals),
        symbol: asset.symbol,
        value: Hex.fromNumber(boundedQuote),
      }
    return undefined
  }, [assetIn, assetOut, boundedQuote, frameMode])

  const host = RemoteRpc.useExternalHost()

  const feePending = !!calls && (prepare.isFetching || quote.isFetching)
  const fieldErrors = !!(amountError || tokenInError || tokenOutError)

  const prepareAlert = usePrepareAlert({
    mutation: { error: confirm.error },
    query: prepare,
    suppress: fieldErrors,
  })
  const alert =
    prepareAlert ??
    (!fieldErrors && quote.isError
      ? {
          type: 'error' as const,
          message: ExecutionError.parse(quote.error).message,
        }
      : null)

  function reject() {
    rpc.reject()
  }

  function submit() {
    capture('swap_submitted', analyticsContext)
    confirm.mutate()
  }

  if (funds.frame) return funds.frame

  return (
    <TransactionFrames.Swap
      actions={funds.actions}
      alert={alert}
      assets={frameAssets}
      assetsIn={frameAssetsIn}
      assetsLoading={
        assetOptions.length === 0 &&
        (assets.isLoading || tokenMetadata.isLoading || pairTokenMetadata.isLoading)
      }
      autoSwap={autoSwap}
      confirming={confirm.isPending}
      errors={{
        amount: amountError,
        tokenIn: tokenInError,
        tokenOut: tokenOutError,
      }}
      fee={fee}
      feePending={feePending && !fee}
      host={host}
      mode={frameMode}
      onChange={(event) => {
        confirm.reset()
        if (event.field === 'amount') {
          const next = event.value.replaceAll(',', '.')
          if (!decimalRegex.test(next)) return
          setValues((current) => ({ ...current, amount: next }))
          return
        }
        if (event.field === 'tokenIn') {
          setValues((current) => ({
            ...current,
            [type === 'buy' ? 'pairToken' : 'token']: event.value,
          }))
          return
        }
        setValues((current) => ({
          ...current,
          [type === 'buy' ? 'token' : 'pairToken']: event.value,
        }))
      }}
      onConfirm={submit}
      onReject={reject}
      quote={{ amount: quoteAmount, loading: quote.isFetching }}
      ready={
        !!prepareData &&
        !!calls &&
        !preparingCurrentInput &&
        !capabilities?.error &&
        !amountError &&
        !tokenInError &&
        !tokenOutError
      }
      selectedAssetIn={assetIn ? toFrameAsset(assetIn) : undefined}
      selectedAssetOut={assetOut ? toFrameAsset(assetOut) : undefined}
      sponsored={sponsored}
      values={{ amount: values.amount, chainId: resolvedChainId }}
    />
  )
}

type SwapAsset = {
  address: Address.Address
  balance: bigint
  decimals: number
  symbol: string
}

function upsertFallback(
  options: Map<string, SwapAsset>,
  address: Address.Address,
  metadata: { decimals?: number | undefined; symbol?: string | undefined } | undefined,
) {
  const key = address.toLowerCase()
  if (options.has(key)) return
  options.set(key, {
    address,
    balance: 0n,
    decimals: metadata?.decimals ?? tip20Decimals,
    symbol:
      metadata?.symbol || (sameAddress(address, Addresses.pathUsd) ? 'pathUSD' : shorten(address)),
  })
}

function toUsdAmount(amount: bigint | undefined, decimals: number | undefined) {
  if (amount === undefined || decimals === undefined) return undefined
  const value = Number(formatUnits(amount, decimals))
  return Number.isFinite(value) ? value : undefined
}

function parseAmount(value: string, decimals: number | undefined) {
  if (!value || value === '.' || decimals === undefined) return undefined
  const normalized = value.startsWith('.')
    ? `0${value}`
    : value.endsWith('.')
      ? value.slice(0, -1)
      : value
  if ((normalized.split('.')[1]?.length ?? 0) > decimals) return undefined
  try {
    return parseUnits(normalized, decimals)
  } catch {
    return undefined
  }
}

function applySlippageDown(amount: bigint, slippage: number) {
  const scaled = BigInt(Math.max(0, Math.round(slippage * Number(slippageScale))))
  if (scaled >= slippageScale) return 0n
  return (amount * (slippageScale - scaled)) / slippageScale
}

function applySlippageUp(amount: bigint, slippage: number) {
  const scaled = BigInt(Math.max(0, Math.round(slippage * Number(slippageScale))))
  return (amount * (slippageScale + scaled) + slippageScale - 1n) / slippageScale
}

function toFrameAsset(asset: SwapAsset) {
  return {
    address: asset.address,
    balance: Token.formatQuantity(asset.balance, asset.decimals),
    symbol: asset.symbol,
  }
}

function findAsset<const asset extends { address: string }>(
  assets: readonly asset[] | undefined,
  token: string | undefined,
) {
  if (!token) return undefined
  return assets?.find((asset) => sameAddress(asset.address, token))
}

function sameAddress(a: string, b: string) {
  return a.toLowerCase() === b.toLowerCase()
}

function shorten(value: string) {
  return formatHash(value)
}
